import React, { useEffect, useState } from 'react';
import { analyticsApi } from '@/api/analytics';
import { TimeSeriesChart } from './TimeSeriesChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, QrCode, Link2, FileText, Globe, Smartphone } from 'lucide-react';

/**
 * "What's actually getting traffic" across every link type, not just QR.
 *
 * The window (period), granularity and the page's tracking filters come from
 * the Analytics page, so this tab follows the same controls as the others.
 * Every request carries the filters in the snake_case names the API reads.
 */

const TYPES = [
  { key: 'all', label: 'All' },
  { key: 'url', label: 'Short URLs' },
  { key: 'qr', label: 'QR codes' },
  { key: 'page', label: 'Landing pages' },
  { key: 'bio', label: 'Bio pages' },
];

const PERIOD_LABEL = { '7d': '7 days', '30d': '30 days', '90d': '90 days', '1y': '12 months' };

const TYPE_ICON = { url: Link2, qr: QrCode, page: FileText, bio: FileText };

const fmt = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

export default function ActivityPanel({ period = '30d', granularity = 'day', filters = {} }) {
  const [type, setType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [series, setSeries] = useState(null);
  const [devices, setDevices] = useState([]);
  const [geo, setGeo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { ...filters, period, type };
    Promise.all([
      analyticsApi.getActivity({ ...params, limit: 20 }),
      analyticsApi.getActivityDevices(params),
      analyticsApi.getActivityGeo(params),
      analyticsApi.getActivityOverTime({ ...params, granularity }),
    ])
      .then(([a, d, g, t]) => {
        if (cancelled) return;
        const asList = (v) => (Array.isArray(v) ? v : []);
        setItems(asList(a.data?.items));
        setDevices(asList(d.data?.devices));
        setGeo(g.data || null);
        setSeries(t?.data || null);
      })
      .catch(() => { if (!cancelled) setError('Could not load activity'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, granularity, type, JSON.stringify(filters)]);

  const maxEvents = items.length ? items[0].events : 0;

  return (
    <div className="space-y-6" data-testid="activity-panel">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {TYPES.map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={type === t.key ? 'default' : 'outline'}
              onClick={() => setType(t.key)}
              data-testid={`activity-type-${t.key}`}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <span className="ml-auto text-sm text-muted-foreground">
          Last {PERIOD_LABEL[period] || period}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading activity…
        </div>
      ) : error ? (
        <Card><CardContent className="py-10 text-center text-destructive">{error}</CardContent></Card>
      ) : (
        <>
          {series && (
            <TimeSeriesChart
              data={series.data_points || []}
              series={series.series}
              title="Activity over time"
              description="Stacked by link type. Days are South African time; bot traffic is excluded."
            />
          )}

          {/* Ranked items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">Most active items</CardTitle>
              <CardDescription>
                QR codes, short URLs, landing pages and bio pages ranked together over the last {PERIOD_LABEL[period] || period}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No activity recorded in this period.
                </p>
              ) : (
                <div className="space-y-2" data-testid="activity-items">
                  {items.map((it) => {
                    const Icon = TYPE_ICON[it.type] || Link2;
                    const pct = maxEvents ? Math.max(2, (it.events / maxEvents) * 100) : 0;
                    return (
                      <div key={`${it.type}-${it.id}`} className="flex items-center gap-3">
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-sm font-medium" title={it.label}>
                              {it.label}
                            </span>
                            <span className="shrink-0 text-sm tabular-nums">{fmt(it.events)}</span>
                          </div>
                          {/* volume bar — the ranking is the point, so show it */}
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-muted">
                            <div className="h-full rounded bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mt-0.5 flex justify-between text-xs text-muted-foreground">
                            <span>{it.type_label}{it.code ? ` · ${it.code}` : ''}</span>
                            <span>last {fmtDate(it.last_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Devices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Smartphone className="h-4 w-4" /> Device breakdown
                </CardTitle>
                <CardDescription>Across every link type</CardDescription>
              </CardHeader>
              <CardContent>
                {devices.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No device data in this period.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="activity-devices">
                    {devices.map((d) => (
                      <div key={d.name} className="flex items-center gap-3">
                        <span className="w-20 shrink-0 text-sm capitalize">{d.name}</span>
                        <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
                          <div
                            className="h-full rounded bg-primary"
                            style={{ width: `${d.percentage || 0}%` }}
                          />
                        </div>
                        <span className="w-24 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                          {fmt(d.count)}
                          {d.percentage != null ? ` · ${Math.round(d.percentage)}%` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Geography */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                  <Globe className="h-4 w-4" /> Geography
                </CardTitle>
                <CardDescription>Visitor country, where resolved</CardDescription>
              </CardHeader>
              <CardContent>
                {geo && !geo.resolved ? (
                  /* Distinguish "not switched on" from "nobody visited" — the
                     events exist, they just have no country against them. */
                  <div className="py-6 text-center text-sm text-muted-foreground" data-testid="geo-unconfigured">
                    <p className="font-medium text-foreground">Location lookup isn’t enabled yet</p>
                    <p className="mt-1">
                      {fmt(geo.total_events)} event{geo.total_events === 1 ? '' : 's'} recorded in this
                      period, none with a country resolved.
                    </p>
                    <p className="mt-2 text-xs">
                      Add the GeoLite2 database to switch this on — historical events can be
                      backfilled, since visitor IPs were captured all along.
                    </p>
                  </div>
                ) : Array.isArray(geo?.countries) && geo.countries.length ? (
                  <div className="space-y-2" data-testid="activity-geo">
                    {geo.countries.slice(0, 8).map((c) => (
                      <div key={c.code} className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm">{c.name || c.code}</span>
                        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                          {fmt(c.count)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    No location data in this period.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
